import { z } from "zod";
import { HomeworkSource } from "./homework.ts";
import {
  HOMEWORK_MATERIAL_MAX,
  HomeworkMaterialFileIds,
  HomeworkTaskText,
} from "./homework-material.ts";
import { UserId } from "./user.ts";
import type { FailureScope } from "./errors.ts";

/**
 * План раздачи домашки: то, что форма выдачи держит между кругами по сети и
 * между жизнями вкладки (5.7 docs/homework-phase2-design.md).
 *
 * Почему это лежит в `@edu/shared`, хотя по проводу не ездит ни одним полем.
 * Две причины, обе про правду, а не про красоту:
 *
 * 1. у `@edu/web` бегунка тестов нет вовсе (раздел 9 CLAUDE.md), а сборка плана
 *    это ровно то место, где ошибка стоит второй домашки: один `requestId` на
 *    двоих означает `409 request_id_reused` второму, а забытый `planRunId`
 *    означает дубль после выгрузки вкладки
 * 2. схема разбора нужна на границе `localStorage` по той же причине, по какой
 *    она нужна на границе сети: план мог записать другой бандл
 *
 * ⚠️ План живёт в `localStorage` преподавателя до 30 суток и содержит ИНИЦИАЛЫ,
 * а не имена (раздел 6 фазы 2). Правило проекта для этого хранилища жёстче
 * («не класть туда ничего, кроме самого ответа», 9.1 docs/homework-design.md), и
 * исключение оплачено тем, что без плана обрыв вкладки на телефоне даёт ученику
 * вторую работу с другими числами. Минимизация соблюдена настолько, насколько
 * возможно: имена сокращены, состав заданий не хранится, завершённый план
 * удаляется сразу.
 */

/**
 * Сколько живёт незавершённый план. Тридцать суток выбраны не наугад: столько
 * же живёт корзина домашек, то есть столько же живёт возможность разгрести
 * последствия дубля.
 *
 * ⚠️ Уборка идёт по признаку «все обслужены», а срок это только предельный
 * возраст. Наоборот было бы хуже: сценарий, ради которого план заведён (ОС
 * выгрузила вкладку, человек ушёл в мессенджер), регулярно означает возврат
 * через день или два.
 */
export const ASSIGN_PLAN_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Сколько сетевых неудач подряд останавливают цикл.
 *
 * Одной мало: единичный сбой на плохом канале это норма, и бросать из-за него
 * десять оставшихся значит наказывать за качество сети. Двенадцати много:
 * человек минуту смотрит на «Создаём… 6 из 12», прежде чем увидит панель.
 */
export const ASSIGN_NETWORK_STREAK_MAX = 2;

/**
 * До скольких учеников «Выбрать всех» доступно без поиска (5.4 фазы 2).
 *
 * Порог из смысла, а не из техники: нажатие разворачивает подтверждение с
 * перечислением имён, и одиннадцать имён человек уже не читает, а проглядывает.
 */
export const ASSIGN_PICK_ALL_LIMIT = 10;

/**
 * Судьба одного ученика в плане.
 *
 * `pending` это И «ещё не отправляли», И «отправили, но ответа не получили»:
 * различать их в хранилище нечем и незачем, обе означают «повторить с тем же
 * ключом безопасно».
 */
export const AssignStatus = z.enum(["pending", "created", "failed"]);
export type AssignStatus = z.infer<typeof AssignStatus>;

export const AssignPlanStudent = z.object({
  userId: UserId,
  /** Инициалы, а не имя: см. шапку файла. Нужны только для читаемости строки. */
  initials: z.string().min(1).max(80),
  /** Ключ идемпотентности создания, свой у каждого ученика (1.4 фазы 2). */
  requestId: z.string().uuid(),
  status: AssignStatus,
});
export type AssignPlanStudent = z.infer<typeof AssignPlanStudent>;

/**
 * Аудио или видео среди файлов материала (Р6 `docs/homework-media-plan.md`):
 * вид и имя, чтобы форма нарисовала плитку файла, а не битую картинку. Порядок
 * и само членство в материале задаёт `materialFileIds`, здесь только подписи к
 * тем его файлам, что не картинки.
 *
 * Длительность и вес необязательны: без них форма просто не скажет, что файл
 * тяжёлый. С ними «Задать ещё раз» и продолжение раздачи предупреждают так же,
 * как первый выбор.
 */
export const HomeworkMaterialMediaRef = z.object({
  fileId: z.string().uuid(),
  kind: z.enum(["audio", "video"]),
  name: z.string(),
  durationMs: z.number().int().nonnegative().nullable().optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
});
export type HomeworkMaterialMediaRef = z.infer<typeof HomeworkMaterialMediaRef>;

export const HomeworkMaterialMediaRefs = z.array(HomeworkMaterialMediaRef).max(HOMEWORK_MATERIAL_MAX);

/** Растёт, когда форма плана меняется: чужой план не разберётся и не помешает. */
export const ASSIGN_PLAN_VERSION = 1;

export const AssignPlan = z.object({
  version: z.literal(ASSIGN_PLAN_VERSION),
  /**
   * ⚠️ Ключ хранения строится от `planRunId`, а НЕ от `batchId`.
   *
   * `batchId` есть только у раздачи на нескольких, а выдача одному ученику
   * раздачей не является (4.2 фазы 2). Строить ключ от него значило бы защитить
   * путь, который делают за столом, и оставить без защиты путь, который делают
   * на бегу: «пришли с карточки ученика, один получатель» это и есть целевые
   * пять минут между уроками, и именно там телефон выгружает вкладку.
   */
  planRunId: z.string().uuid(),
  /** `null` означает «раздачи нет», то есть получатель ровно один. */
  batchId: z.string().uuid().nullable(),
  createdAt: z.number().int(),
  /**
   * ⚠️ Необязателен с 26.09.2026 (2 `docs/homework-scratch-design.md`): домашка
   * бывает без источника заданий. Все три новых поля необязательные И В ТОЙ ЖЕ
   * ВЕРСИИ 1, а не в новой: подъём `ASSIGN_PLAN_VERSION` заставил бы
   * `sweepPlans` молча выбросить старые планы вместе с ключами идемпотентности,
   * и в день выкатки продолженная раздача дала бы дубли. Старый план без новых
   * полей читается как план без материала, что и было правдой.
   */
  source: HomeworkSource.optional(),
  /**
   * Текст задания. ⚠️ Свободный текст, и имя ученика в нём будет («Маша,
   * реши...»), а план сознательно держит только инициалы. Принято осознанно:
   * это устройство самого преподавателя, и план удаляется по завершении
   * раздачи тем же путём, что сегодня (2 проекта).
   */
  taskText: HomeworkTaskText.optional(),
  /**
   * Картинки материала: уже загруженные файлы, у всех домашек раздачи одни и
   * те же. По ним же при возобновлении восстанавливаются миниатюры.
   */
  materialFileIds: HomeworkMaterialFileIds.optional(),
  /**
   * Какие из `materialFileIds` аудио и видео. ⚠️ Необязательно и в версии 1 по
   * той же причине, что поля выше: план, сохранённый до 16.10.2026, без него
   * читается как план без аудио, и это было правдой.
   */
  materialMedia: HomeworkMaterialMediaRefs.optional(),
  dueAt: z.number().int().nullable(),
  title: z.string().max(200).nullable(),
  /** Подпись «задана после занятия» принадлежит паре, значит и плану на одного. */
  lessonId: z.string().uuid().nullable(),
  students: z.array(AssignPlanStudent).min(1),
});
export type AssignPlan = z.infer<typeof AssignPlan>;

/**
 * Инициалы из имени: «Аня Смирнова» становится «А. Смирнова».
 *
 * Одно слово остаётся как есть: сокращать «Марко» не во что, а пустая строка в
 * предложении «продолжить раздачу для 12 учеников» читалась бы как поломка.
 * Отчество отбрасывается вместе с остальной серединой: в плане нужен признак
 * «тот ли это человек», а не паспортные данные.
 */
export function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter((p) => p !== "");
  if (parts.length === 0) return "ученик";
  const first = parts[0] ?? "";
  if (parts.length === 1) return first;
  const last = parts[parts.length - 1] ?? "";
  // Array.from, а не [0]: имя может начинаться с суррогатной пары
  const letter = Array.from(first)[0] ?? "";
  return `${letter}. ${last}`;
}

/**
 * План одного нажатия «Задать».
 *
 * ⚠️ Идентификаторы минтятся ЗДЕСЬ и по одному на ученика. Общий `requestId` на
 * двоих означает, что второму сервер ответит `409 request_id_reused` (сверка
 * ученика в 4.2), и это лучше молчаливого возврата чужой карточки, но всё равно
 * поломка клиента.
 *
 * `newId` параметром, а не вызовом `crypto` внутри: функция обязана быть чистой,
 * иначе её нечем проверить, а проверять здесь надо именно уникальность ключей.
 */
export function makeAssignPlan(args: {
  students: readonly { userId: string; displayName: string }[];
  /** Отсутствие означает домашку без источника заданий. */
  source?: HomeworkSource | undefined;
  taskText?: string | undefined;
  materialFileIds?: readonly string[] | undefined;
  materialMedia?: readonly HomeworkMaterialMediaRef[] | undefined;
  dueAt: number | null;
  title: string | null;
  lessonId: string | null;
  now: number;
  newId: () => string;
}): AssignPlan {
  return {
    version: ASSIGN_PLAN_VERSION,
    planRunId: args.newId(),
    // Раздачей выдача одному не является: придумывать ей группу незачем (1.5)
    batchId: args.students.length > 1 ? args.newId() : null,
    createdAt: args.now,
    // Поля нет вовсе, а не `undefined`: план уходит в `localStorage` и
    // разбирается схемой, где отсутствие и есть «без источника»
    ...(args.source === undefined ? {} : { source: args.source }),
    ...(args.taskText === undefined ? {} : { taskText: args.taskText }),
    ...(args.materialFileIds === undefined ? {} : { materialFileIds: [...args.materialFileIds] }),
    ...(args.materialMedia === undefined ? {} : { materialMedia: [...args.materialMedia] }),
    dueAt: args.dueAt,
    title: args.title,
    lessonId: args.lessonId,
    students: args.students.map((s) => ({
      userId: s.userId,
      initials: initialsOf(s.displayName),
      requestId: args.newId(),
      status: "pending",
    })),
  };
}

/**
 * Кому работа ещё не досталась.
 *
 * Отказ по ученику (`failed`) попадает сюда наравне с неотправленным: «Повторить
 * для оставшихся» это одна кнопка на всех необслуженных, а не две кнопки на два
 * вида неудачи. Отвязанного она повторит и снова получит отказ, зато ученика,
 * которого успели привязать обратно, обслужит.
 */
export function unservedStudents(plan: AssignPlan): AssignPlanStudent[] {
  return plan.students.filter((s) => s.status !== "created");
}

export function assignPlanProgress(plan: AssignPlan): { total: number; created: number } {
  return {
    total: plan.students.length,
    created: plan.students.filter((s) => s.status === "created").length,
  };
}

/**
 * План отработан целиком, и это единственная причина удалить его сразу.
 *
 * ⚠️ Отсюда же правило «повторное нажатие „Задать" после успешной раздачи минтит
 * новые идентификаторы» (4.2 фазы 2): закрытый план не переиспользуется, иначе
 * человек, задающий вторую тему тем же двенадцати, получил бы 200 со старыми
 * работами и решил, что фича сломалась.
 */
export function isAssignPlanSettled(plan: AssignPlan): boolean {
  return plan.students.every((s) => s.status === "created");
}

/** Судьба одного ученика поменялась. Копией, а не правкой на месте. */
export function withAssignStatus(
  plan: AssignPlan,
  userId: string,
  status: AssignStatus,
): AssignPlan {
  return {
    ...plan,
    students: plan.students.map((s) => (s.userId === userId ? { ...s, status } : s)),
  };
}

/**
 * Ограничитель цикла: что делать после ответа по очередному ученику (1.6 фазы 2).
 *
 * Считает подряд идущие сетевые неудачи и говорит, продолжать ли. Отдельной
 * чистой функцией, потому что это правило, а не поток управления: в цикле оно
 * выглядело бы двумя счётчиками и тремя `break`, и проверить его было бы нечем.
 */
export function assignGuardAfter(
  networkStreak: number,
  outcome: FailureScope | "created",
): { networkStreak: number; stop: boolean } {
  // Дело в задании: следующие одиннадцать ответят тем же самым
  if (outcome === "all") return { networkStreak: 0, stop: true };
  if (outcome === "network") {
    const streak = networkStreak + 1;
    return { networkStreak: streak, stop: streak >= ASSIGN_NETWORK_STREAK_MAX };
  }
  // Успех и отказ по ученику одинаково означают, что связь есть
  return { networkStreak: 0, stop: false };
}
